<template>
    <div class="card">
        <div class="card-body">
            <h5 class="card-title" v-if="featureTitle">{{ featureTitle }}</h5>
            <h6 class="card-subtitle" v-if="featureSubTitle" >{{ featureSubTitle }}</h6>

            <table class="">
                <tbody class="">
                    <tr
                        v-for="item in featurePropertiesTransformed"
                        :key="`prop-${item.key}`">
                        <td class="">{{ item.label }}</td>
                        <td>
                            <ul class="list-group list-group-flush">
                                <template v-for="(res, resIdx) in item.val">
                                    <li class="list-group-item"
                                        :key="`${item.keyBind}-${resIdx}`">
                                        <template v-if="item.type == 'image'">
                                            <a
                                                :href="res.val"
                                                :target="item?.params?.target ?? '_blank'">
                                                <img
                                                    :src="res.val"
                                                    :alt="`image`"
                                                    :class="['img-fluid', item?.params?.class ?? '']">
                                            </a>
                                        </template>
                                        <template v-else-if="item.type == 'link'">
                                            <a
                                                :href="res.val"
                                                :target="item?.params?.target ?? '_blank'"
                                                :class="item?.params?.class ?? ''">
                                                {{ res.text }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            <div v-html="res.text">
                                            </div>
                                        </template>
                                    </li>
                                </template>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>

        </div>
        <div class="card-footer">
            <a href="#"
                class="btn btn-secondary-main d-flex align-items-center justify-content-center"
                @click.prevent="onSelected">
                <i class="fas fa-check"></i>
                <span>
                    {{ $t(`${translateID}.actions.select`) }}
                </span>
            </a>
        </div>
    </div>
</template>

<script>
    import {formatString, formatUrl, isString, slugify} from '@config/utils';

    export default {
        props: {
            value: {
                type: Object,
                default: () => {
                    return {}
                },
            },
            translateID: {
                type: String,
                default: 'featureable',
            },
            title: {
                type: String,
                default: '',
            },
            featureableConfig: {
                type: Object,
                default: () => {
                    return {}
                },
            },
        },
        data() {
            return {
            }
        },
        computed: {
            feature() {
                return this.value ?? {};
            },
            featureProperties() {
                return this.feature?.properties ?? {};
            },
            configTypenames() {
                return this.featureableConfig?.typenames ?? [];
            },
            typenameConfig() {
                let typename = this.extractNameFromFid(this.feature.id);
                return this.configTypenames.find(t => t.typename == typename) ?? {};
            },
            typenameProperties() {
                return this.typenameConfig?.properties ?? [];
            },
            visibleFeaturePropertiesKeys() {
                return this.typenameProperties.map(item => item.source_name) ?? [];
            },
            featureTitle() {
                let title = null;

                let typenameConfig = this.typenameConfig;
                let propertyTitle = typenameConfig.property_title ?? typenameConfig.title;
                if(propertyTitle) {
                    title = formatString(propertyTitle, this.featureProperties, '{', '}');
                }

                return title ?? this.title;
            },
            featureSubTitle() {
                return this.title && this.title != this.featureTitle ? this.title : null;
            },
            featurePropertiesTransformed() {
                return Object.keys(this.featureProperties)
                    .filter(key => this.visibleFeaturePropertiesKeys.length == 0 ||
                        this.visibleFeaturePropertiesKeys.length && this.visibleFeaturePropertiesKeys.includes(key))
                    .map((key, index) => {
                        const cfgProperty = this.typenameProperties.find(item => item.source_name == key);
                        const _type = (cfgProperty?.type ?? 'string').toLowerCase();
                        const _params = cfgProperty?.params ?? {separator: ','};
                        const _label = cfgProperty?.name ?? key;
                        let _text = this.featureProperties[key];
                        _text = isString(_text) ? _text : JSON.stringify(_text);
                        let _value = [];
                        switch (_type) {
                            case 'image':
                            case 'link':
                                let _vals = [];
                                if(_params.separator) {
                                    _vals = _text.split(_params.separator).map(item => item.trim()).filter(x => x);
                                }
                                _value = _vals.map((val, idx) => {
                                    let href = _params.href ?? val;
                                    href = href ? formatUrl(href, this.featureProperties, '{', '}') : null;
                                    if(href) {
                                        let scheme = `${window.location.protocol}//`;
                                        href = href.replace('http://', scheme);
                                        href = href.split(scheme).filter(x => x).slice(-1).pop();
                                        href = href ? `${scheme}${href}` : null;
                                    }
                                    return {
                                        text: this.textVal(val),
                                        val: href,
                                        keyBind: `prop-${index}-${slugify(key)}-${slugify(_type)}-${idx}`,
                                    }
                                });
                                break;
                            default:
                                _value.push({
                                    text: this.textVal(_text),
                                    val: _text,
                                    keyBind: `prop-${index}-${slugify(key)}-${slugify(_type)}`,
                                });
                                break;
                        }

                        return {
                            key: key,
                            label: _label,
                            val: _value,
                            type: _type,
                            params: _params
                        }
                    });
            },
        },
        methods: {
            extractNameFromFid(fid){
                if(fid.indexOf('.fid') !== -1){
                    let parts = fid.split(".fid");
                    return parts[0];
                }
                return fid;
            },
            textVal(val){
                return val ? val.trim().replace('null', '').replace('""', '') : '';
            },
            onSelected() {
                this.$emit('selected', JSON.parse(JSON.stringify({
                    feature: this.feature,
                    typename: this.typenameConfig,
                    transformed: {
                        title: this.featureTitle,
                        subtitle: this.featureSubTitle,
                        properties: this.featurePropertiesTransformed.map(p => {
                            return {
                                key: p.label,
                                value: p.val.map(v => {
                                    return this.textVal(v.val)
                                }).join(', '),
                            }
                        }).reduce((obj, item) => Object.assign(obj, { [item.key]: item.value }), {})
                    }
                })));
            }
        },
    }
</script>

<style lang="scss" scoped>
@import "@styles/bootstrap/_mixins.scss";
@import "@styles/_mixins.scss";
.card {
    .card-header {
        padding: .75rem .8rem;
    }

    .card-body {
        padding: .8rem;

        .card-title {
            font-family: var(--city-semibold-font-family);
            color: var(--primary-color);
        }
        .card-subtitle {
            font-family: var(--city-font-family);
            @include font-size(1rem);
            margin-bottom: .8rem;
            font-weight: normal;
            color: #9e9e9e;
        }
        .card-text {
            @include font-size(1rem);
            font-family: var(--city-font-family);
        }

        table {
            border-collapse: collapse;
            border-spacing: 0;
            font-family: var(--city-font-family);
            width: 100%;

            tbody {
                background-color: #ffffff;
                @include font-size(.9rem);

                tr {
                    border-bottom: solid 1px #dddddd;
                }

                td {
                    &:first-child {
                        padding: .25rem .5rem .25rem 0;
                        font-family: var(--city-semibold-font-family);
                        vertical-align: top;
                        min-width: 120px;
                    }

                    &:last-child {
                        text-align: right;
                        word-break: break-word;
                        overflow-wrap: break-word;
                        color: #6e6e6e;

                        ul li {
                            padding: .25rem 0 .25rem .5rem;
                        }
                    }
                }
            }
        }
    }
    .card-footer {
        padding: .5rem .8rem;

        .entity-wrap {
            img {
                max-width: 35px;
                margin-right: 10px;
            }

            span {
                margin-right: 10px;
            }
        }

        .btn {
            padding: .3rem .6rem;
            margin: 0;
            min-width: auto;

            i {
                @include font-size(1rem);
                padding-right: 8px;
            }

            span {
                @include font-size(.875rem);
                font-family: var(--city-bold-font-family);
            }
        }

    }
}
</style>